/**
 * Connector utility functions for anchor point calculation and snapping
 */

import { EditableElement, AnchorPoint } from '../editing/types';

/**
 * Check if an element is a shape element
 * @param element - The element to check
 * @returns true if the element is a shape, false otherwise
 */
export function isShapeElement(element: EditableElement): boolean {
  return element.type === 'shape';
}

/**
 * Calculate all anchor points on a shape's edges
 * @param element - The element to calculate anchor points for
 * @param offset - Optional offset in pixels to position anchor points outside shape edges (default: 0 for edge positions)
 */
export function calculateAnchorPoints(
  element: EditableElement,
  offset: number = 0
): Array<{ side: 'top' | 'right' | 'bottom' | 'left'; x: number; y: number }> {
  const { x, y, width, height, rotation = 0 } = element;
  
  // Calculate element center
  const centerX = x + width / 2;
  const centerY = y + height / 2;
  
  // Calculate anchor points in local (non-rotated) coordinates relative to center
  const localAnchors = [
    // Top edge - offset upward if offset > 0
    { side: 'top' as const, localX: 0, localY: -height / 2 - offset },
    // Right edge - offset rightward if offset > 0
    { side: 'right' as const, localX: width / 2 + offset, localY: 0 },
    // Bottom edge - offset downward if offset > 0
    { side: 'bottom' as const, localX: 0, localY: height / 2 + offset },
    // Left edge - offset leftward if offset > 0
    { side: 'left' as const, localX: -width / 2 - offset, localY: 0 },
  ];
  
  // If no rotation, return anchors in world coordinates
  if (rotation === 0 || !rotation) {
    return localAnchors.map(anchor => ({
      side: anchor.side,
      x: centerX + anchor.localX,
      y: centerY + anchor.localY,
    }));
  }
  
  // Apply rotation around element center
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  
  return localAnchors.map(anchor => {
    // Rotate the local point around origin (0, 0)
    const rotatedX = anchor.localX * cos - anchor.localY * sin;
    const rotatedY = anchor.localX * sin + anchor.localY * cos;
    
    // Translate to world coordinates
    return {
      side: anchor.side,
      x: centerX + rotatedX,
      y: centerY + rotatedY,
    };
  });
}

/**
 * Find the best anchor point based on relative position of two elements
 */
export function findBestAnchor(
  fromElement: EditableElement,
  toElement: EditableElement,
  fromAnchor?: AnchorPoint
): AnchorPoint {
  const fromCenterX = fromElement.x + fromElement.width / 2;
  const fromCenterY = fromElement.y + fromElement.height / 2;
  const toCenterX = toElement.x + toElement.width / 2;
  const toCenterY = toElement.y + toElement.height / 2;
  
  const dx = toCenterX - fromCenterX;
  const dy = toCenterY - fromCenterY;
  
  // Determine which side of the target element to connect to
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  
  let side: 'top' | 'right' | 'bottom' | 'left';
  let x: number;
  let y: number;
  
  if (absDx > absDy) {
    // Horizontal connection
    if (dx > 0) {
      side = 'left';  // Connect to left side of target
      x = toElement.x;
      y = toElement.y + toElement.height / 2;
    } else {
      side = 'right';  // Connect to right side of target
      x = toElement.x + toElement.width;
      y = toElement.y + toElement.height / 2;
    }
  } else {
    // Vertical connection
    if (dy > 0) {
      side = 'top';  // Connect to top side of target
      x = toElement.x + toElement.width / 2;
      y = toElement.y;
    } else {
      side = 'bottom';  // Connect to bottom side of target
      x = toElement.x + toElement.width / 2;
      y = toElement.y + toElement.height;
    }
  }
  
  return {
    side,
    x,
    y,
    isPrecise: false,
  };
}

/**
 * Snap to nearest anchor point on an element
 */
export function snapToAnchor(
  pointerPos: { x: number; y: number },
  element: EditableElement,
  threshold: number = 30
): AnchorPoint | null {
  const anchors = calculateAnchorPoints(element);
  
  let nearestAnchor: { side: 'top' | 'right' | 'bottom' | 'left'; x: number; y: number } | null = null;
  let minDistance = Infinity;
  
  for (const anchor of anchors) {
    const distance = Math.sqrt(
      Math.pow(pointerPos.x - anchor.x, 2) + Math.pow(pointerPos.y - anchor.y, 2)
    );
    
    if (distance < minDistance && distance <= threshold) {
      minDistance = distance;
      nearestAnchor = anchor;
    }
  }
  
  if (nearestAnchor) {
    return {
      side: nearestAnchor.side,
      x: nearestAnchor.x,
      y: nearestAnchor.y,
      isPrecise: false,
    };
  }
  
  return null;
}

/**
 * Calculate precise anchor point (when Cmd/Ctrl is held)
 */
export function calculatePreciseAnchor(
  pointerPos: { x: number; y: number },
  element: EditableElement
): AnchorPoint {
  const { x, y, width, height } = element;
  
  // Clamp pointer position to element bounds
  const clampedX = Math.max(x, Math.min(x + width, pointerPos.x));
  const clampedY = Math.max(y, Math.min(y + height, pointerPos.y));
  
  // Determine which side is closest
  const distToLeft = clampedX - x;
  const distToRight = (x + width) - clampedX;
  const distToTop = clampedY - y;
  const distToBottom = (y + height) - clampedY;
  
  const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom);
  
  let side: 'top' | 'right' | 'bottom' | 'left';
  if (minDist === distToLeft) {
    side = 'left';
  } else if (minDist === distToRight) {
    side = 'right';
  } else if (minDist === distToTop) {
    side = 'top';
  } else {
    side = 'bottom';
  }
  
  return {
    side,
    x: clampedX,
    y: clampedY,
    isPrecise: true,
  };
}

/**
 * Get element bounds for hit testing
 */
export function getElementBounds(element: EditableElement): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return {
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
  };
}

/**
 * Check if a point is inside an element
 */
export function isPointInElement(
  point: { x: number; y: number },
  element: EditableElement
): boolean {
  const bounds = getElementBounds(element);
  return (
    point.x >= bounds.x &&
    point.x <= bounds.x + bounds.width &&
    point.y >= bounds.y &&
    point.y <= bounds.y + bounds.height
  );
}

/**
 * Check if a point is within the expanded hover area of an element (including anchor point regions)
 * This includes the element bounds plus anchor point areas (18px offset + anchor radius)
 */
export function isPointNearElement(
  point: { x: number; y: number },
  element: EditableElement,
  anchorOffset: number = 18,
  anchorRadius: number = 6
): boolean {
  const bounds = getElementBounds(element);
  
  // Check if point is inside element bounds
  if (isPointInElement(point, element)) {
    return true;
  }
  
  // Check if point is near any anchor point
  const anchors = calculateAnchorPoints(element, anchorOffset);
  const hoverRadius = anchorRadius + 5; // Add 5px padding for easier hover detection
  
  for (const anchor of anchors) {
    const distance = Math.sqrt(
      Math.pow(point.x - anchor.x, 2) + Math.pow(point.y - anchor.y, 2)
    );
    if (distance <= hoverRadius) {
      return true;
    }
  }
  
  return false;
}

/**
 * Find element at a given point (including anchor point hover areas)
 */
export function findElementAtPoint(
  point: { x: number; y: number },
  elements: EditableElement[]
): EditableElement | null {
  // Check in reverse order (top to bottom) to get the topmost element
  for (let i = elements.length - 1; i >= 0; i--) {
    const element = elements[i];
    // Skip connectors themselves
    if (element.type === 'connector') continue;
    
    // Check if point is in element or near anchor points
    if (isPointNearElement(point, element)) {
      return element;
    }
  }
  
  return null;
}

/**
 * Find nearby shape within threshold distance from an anchor point
 */
export function findNearbyShape(
  anchor: AnchorPoint,
  sourceElementId: string,
  elements: EditableElement[],
  threshold: number = 100
): EditableElement | null {
  let nearestElement: EditableElement | null = null;
  let minDistance = Infinity;

  for (const element of elements) {
    // Skip connectors and the source element
    if (element.type === 'connector' || element.id === sourceElementId) {
      continue;
    }

    // Calculate distance from anchor to element center
    const elementCenterX = element.x + element.width / 2;
    const elementCenterY = element.y + element.height / 2;
    
    const distance = Math.sqrt(
      Math.pow(anchor.x - elementCenterX, 2) + Math.pow(anchor.y - elementCenterY, 2)
    );

    if (distance < minDistance && distance <= threshold) {
      minDistance = distance;
      nearestElement = element;
    }
  }

  return nearestElement;
}

/**
 * Find the nearest anchor point on a shape to a given point
 */
export function findNearestAnchorOnShape(
  point: { x: number; y: number },
  element: EditableElement
): AnchorPoint {
  // Use edge positions (offset = 0) for connector attachment
  const anchors = calculateAnchorPoints(element, 0);
  
  let nearestAnchor: { side: 'top' | 'right' | 'bottom' | 'left'; x: number; y: number } | null = null;
  let minDistance = Infinity;
  
  for (const anchor of anchors) {
    const distance = Math.sqrt(
      Math.pow(point.x - anchor.x, 2) + Math.pow(point.y - anchor.y, 2)
    );
    
    if (distance < minDistance) {
      minDistance = distance;
      nearestAnchor = anchor;
    }
  }
  
  if (nearestAnchor) {
    return {
      side: nearestAnchor.side,
      x: nearestAnchor.x,
      y: nearestAnchor.y,
      isPrecise: false,
    };
  }
  
  // Fallback to center
  return {
    side: 'center',
    x: element.x + element.width / 2,
    y: element.y + element.height / 2,
    isPrecise: false,
  };
}

/**
 * Convert an anchor point with offset back to edge position
 * Used when anchor points are visually offset but connectors need edge positions
 */
export function getEdgeAnchorFromOffsetAnchor(
  anchor: AnchorPoint,
  element: EditableElement,
  offset: number
): AnchorPoint {
  // Recalculate edge position from offset position
  const edgeAnchors = calculateAnchorPoints(element, 0);
  const edgeAnchor = edgeAnchors.find(a => a.side === anchor.side);
  
  if (edgeAnchor) {
    return {
      side: anchor.side,
      x: edgeAnchor.x,
      y: edgeAnchor.y,
      isPrecise: anchor.isPrecise,
    };
  }
  
  // Fallback to original anchor if side not found
  return anchor;
}

